import React, { Fragment, useState, useRef, useEffect, useCallback } from "react";
import { Table } from "reactstrap";
import { useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel, flexRender } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { DebouncedInput } from "helpers/common_helper";
import Pagination from "components/Common/Pagination";
import { FadeLoader } from "react-spinners";
import { getPortalLinks } from "store/actions";

const PortalLinksDataTable = ({
    columns,
    data,
    tableClass,
    isPagination,
    SearchPlaceholder,
    totalrows,
    loading,
    refreshTrigger,
}) => {
    const [globalFilter, setGlobalFilter] = useState("");
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize] = useState(10);
    const [searchString, setSearchString] = useState("");
    const [selectedSortData, setSelectedSortData] = useState({
        value: "created_at",
        direction: "desc",
    });

    const tableRef = useRef();
    const dispatch = useDispatch();

    // Use a ref to track the previous refreshTrigger so we know when a mutation happened
    const prevRefreshTrigger = useRef(refreshTrigger);

    const fetchData = useCallback((pg, search, sort) => {
        dispatch(getPortalLinks({
            pagesize: pageSize,
            currentpage: pg + 1,
            sortorder: sort?.value && sort?.direction
                ? { field: sort.value, direction: sort.direction }
                : { field: "created_at", direction: "desc" },
            searchstring: search || "",
            filter: { status: "" },
        }));
    }, [dispatch, pageSize]);

    // Stable onChange for DebouncedInput — must not change reference on re-render
    const handleSearchChange = useCallback((value) => {
        setSearchString(String(value));
        setPageIndex(0);
    }, []);

    // Fetch on initial mount and whenever pagination/search/sort changes
    useEffect(() => {
        fetchData(pageIndex, searchString, selectedSortData);
    }, [pageIndex, searchString, selectedSortData.value, selectedSortData.direction]);

    // Fetch when a mutation (add/update/delete) occurs — keep current page
    useEffect(() => {
        if (refreshTrigger !== prevRefreshTrigger.current) {
            prevRefreshTrigger.current = refreshTrigger;
            fetchData(pageIndex, searchString, selectedSortData);
        }
    }, [refreshTrigger]);

    const table = useReactTable({
        columns,
        data,
        state: { globalFilter },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
        pageCount: Math.ceil(totalrows / pageSize),
    });

    const serialNumberColumn = {
        header: "#",
        id: "serial",
        cell: ({ row }) => (pageIndex * pageSize) + row.index + 1,
        enableSorting: false,
        enableColumnFilter: false,
    };

    table.setOptions((prev) => ({
        ...prev,
        columns: [serialNumberColumn, ...columns]
    }));

    const { getHeaderGroups, getRowModel } = table;

    const totalPages = Math.ceil(totalrows / pageSize);

    const handlePageChange = (newPageIndex) => {
        setPageIndex(newPageIndex);
    };

    return (
        <Fragment>
            <div className="d-flex justify-content-between gap-2 mb-2 pt-4">
                <div className="d-flex justify-content-between gap-2 align-items-center"></div>
                <div className="d-flex justify-content-between align-items-end">
                    <DebouncedInput
                        value={searchString ?? ""}
                        onChange={handleSearchChange}
                        className="form-control search-box me-2 d-inline-block"
                        placeholder={SearchPlaceholder}
                    />
                </div>
            </div>

            <div className="table-responsive" id="table-to-print" ref={tableRef}>
                <Table hover className={tableClass} bordered>
                    <thead>
                        {getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) =>
                                    !header.isPlaceholder ? (
                                        <th
                                            key={header.id}
                                            style={{ verticalAlign: "middle" }}
                                        >
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                                                {(header.column.columnDef.accessorKey && header.column.columnDef.showFilter !== false) && (
                                                    <span
                                                        className="ms-1"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const field = header.column.columnDef.accessorKey;
                                                            if (!field) return;

                                                            setSelectedSortData((prev) => {
                                                                if (!prev || prev.value !== field) {
                                                                    return { value: field, direction: "asc" };
                                                                } else if (prev.direction === "asc") {
                                                                    return { value: field, direction: "desc" };
                                                                } else {
                                                                    return { value: field, direction: "asc" };
                                                                }
                                                            });
                                                            setPageIndex(0);
                                                        }}
                                                    >
                                                        {selectedSortData?.value === header.column.columnDef.accessorKey ? (
                                                            selectedSortData.direction === "asc" ? (
                                                                <i className="mdi mdi-arrow-up" style={{ fontSize: "20px" }}></i>
                                                            ) : (
                                                                <i className="mdi mdi-arrow-down" style={{ fontSize: "20px" }}></i>
                                                            )
                                                        ) : (
                                                            <i className="mdi mdi-swap-vertical" style={{ fontSize: "20px" }}></i>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </th>) : null
                                )}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={columns.length + 1} className="text-center border-none">
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                                        <FadeLoader color="#00a895" size={40} />
                                    </div>
                                </td>
                            </tr>
                        ) : (data?.length <= 0 || getRowModel()?.rows?.length === 0) ? (
                            <tr>
                                <td colSpan={columns.length + 1} className="text-center">
                                    No Data Found
                                </td>
                            </tr>
                        ) : (
                            getRowModel().rows.map((row) => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} style={{ verticalAlign: "middle" }}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>

            {(isPagination && totalrows > 0) && (
                <Pagination
                    currentPage={pageIndex + 1}
                    totalPages={totalPages}
                    totalItems={totalrows}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    showInfo={true}
                />
            )}
        </Fragment>
    );
};

export default PortalLinksDataTable;
